import { useState, useCallback, useMemo, useRef } from "react";
import { useToast } from "../../components/ui/Toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  File,
  FileText,
  FileDoc,
  Plus,
  Trash,
  MathOperations,
  Folder,
  FolderOpen,
  CaretRight,
  FolderPlus,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { DocumentListItem } from "../../types/document";
import { useDocumentStore } from "../../stores/document";

interface FileTreeProps {
  documents: DocumentListItem[];
  activeDocumentId: string | null;
  onSelect: (id: string) => void;
  projectId: string;
}

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

function getFileIcon(filename: string) {
  if (filename.endsWith(".tex")) return MathOperations;
  if (filename.endsWith(".md")) return FileText;
  if (filename.endsWith(".bib")) return FileText;
  if (filename.endsWith(".docx")) return FileDoc;
  return File;
}

interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  children: TreeNode[];
  document?: DocumentListItem;
}

function buildTree(docs: DocumentListItem[]): TreeNode[] {
  const root: TreeNode[] = [];
  const folderMap = new Map<string, TreeNode>();

  const getOrCreateFolder = (path: string): TreeNode => {
    if (folderMap.has(path)) return folderMap.get(path)!;
    const parts = path.split("/");
    const name = parts[parts.length - 1];
    const node: TreeNode = { name, path, isFolder: true, children: [] };
    folderMap.set(path, node);

    if (parts.length > 1) {
      const parentPath = parts.slice(0, -1).join("/");
      const parent = getOrCreateFolder(parentPath);
      parent.children.push(node);
    } else {
      root.push(node);
    }
    return node;
  };

  for (const doc of docs) {
    const parts = doc.filename.split("/");
    if (parts.length > 1) {
      const folderPath = parts.slice(0, -1).join("/");
      const folder = getOrCreateFolder(folderPath);
      folder.children.push({
        name: parts[parts.length - 1],
        path: doc.filename,
        isFolder: false,
        children: [],
        document: doc,
      });
    } else {
      root.push({
        name: doc.filename,
        path: doc.filename,
        isFolder: false,
        children: [],
        document: doc,
      });
    }
  }

  const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes.sort((a, b) => {
      if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  };

  const sortAll = (nodes: TreeNode[]): TreeNode[] => {
    const sorted = sortNodes(nodes);
    for (const node of sorted) {
      if (node.isFolder) node.children = sortAll(node.children);
    }
    return sorted;
  };

  return sortAll(root);
}

function FileNode({
  node,
  depth,
  activeDocumentId,
  onSelect,
  expandedFolders,
  toggleFolder,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  activeDocumentId: string | null;
  onSelect: (id: string) => void;
  expandedFolders: Set<string>;
  toggleFolder: (path: string) => void;
  onDelete: (id: string) => void;
}) {
  const isExpanded = expandedFolders.has(node.path);

  if (node.isFolder) {
    return (
      <>
        <button
          onClick={() => toggleFolder(node.path)}
          className={cn(
            "group flex w-full items-center gap-1.5 py-1 text-left text-[12px]",
            "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
            "transition-colors duration-75",
          )}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={spring}
            className="shrink-0"
          >
            <CaretRight size={10} weight="bold" className="text-[var(--text-muted)]" />
          </motion.div>
          {isExpanded ? (
            <FolderOpen size={14} weight="fill" className="text-[var(--color-cool-azure)] shrink-0" />
          ) : (
            <Folder size={14} weight="fill" className="text-[var(--text-muted)] shrink-0" />
          )}
          <span className="flex-1 truncate font-medium">{node.name}</span>
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              {node.children.map((child) => (
                <FileNode
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  activeDocumentId={activeDocumentId}
                  onSelect={onSelect}
                  expandedFolders={expandedFolders}
                  toggleFolder={toggleFolder}
                  onDelete={onDelete}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  const doc = node.document;
  if (!doc) return null;
  const Icon = getFileIcon(node.name);
  const isActive = doc.id === activeDocumentId;

  return (
    <button
      onClick={() => onSelect(doc.id)}
      className={cn(
        "group flex w-full items-center gap-2 py-1.5 text-left text-[12px]",
        "transition-colors duration-75",
        isActive
          ? "bg-[var(--color-lever-orange)]/8 text-[var(--text-primary)]"
          : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
      )}
      style={{ paddingLeft: `${8 + depth * 14}px` }}
    >
      <Icon
        size={13}
        weight={isActive ? "fill" : "regular"}
        className={cn("shrink-0", isActive ? "text-[var(--color-lever-orange)]" : "")}
      />
      <span className="flex-1 truncate">{node.name}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }}
        className={cn(
          "flex items-center justify-center rounded-[var(--radius-sm)] p-0.5 mr-1",
          "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
          "hover:text-[var(--color-soft-red)]",
          "transition-all duration-75",
        )}
        aria-label="Delete file"
      >
        <Trash size={11} />
      </button>
    </button>
  );
}

export function FileTree({ documents, activeDocumentId, onSelect, projectId }: FileTreeProps) {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFilename, setNewFilename] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const { createDocument, deleteDocument } = useDocumentStore();

  const tree = useMemo(() => buildTree(documents), [documents]);

  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const isSubmittingRef = useRef(false);

  const handleCreate = useCallback(async () => {
    if (isSubmittingRef.current) return;
    if (!newFilename.trim()) {
      setCreating(false);
      setCreatingFolder(false);
      setNewFilename("");
      return;
    }
    isSubmittingRef.current = true;
    const filename = newFilename.trim();
    try {
      if (creatingFolder) {
        const folderFile = `${filename}/.gitkeep`;
        const docType = "latex" as const;
        await createDocument(projectId, folderFile, docType);
        setExpandedFolders((prev) => new Set(prev).add(filename));
      } else {
        const docType = filename.endsWith(".md") ? ("markdown" as const) : filename.endsWith(".docx") ? ("docx" as const) : ("latex" as const);
        const doc = await createDocument(projectId, filename, docType);
        onSelect(doc.id);
      }
    } catch {
      toast.error("Create failed", `Could not create "${filename}".`);
    } finally {
      setNewFilename("");
      setCreating(false);
      setCreatingFolder(false);
      isSubmittingRef.current = false;
    }
  }, [newFilename, creatingFolder, projectId, createDocument, onSelect, toast]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Files
        </span>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => { setCreatingFolder(true); setCreating(true); }}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
            )}
            aria-label="New folder"
          >
            <FolderPlus size={13} />
          </button>
          <button
            onClick={() => { setCreatingFolder(false); setCreating(true); }}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
            )}
            aria-label="New file"
          >
            <Plus size={13} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <AnimatePresence>
          {creating && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden px-2 mb-1"
            >
              <input
                value={newFilename}
                onChange={(e) => setNewFilename(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                    handleCreate();
                  }
                  if (e.key === "Escape") {
                    isSubmittingRef.current = true;
                    setCreating(false);
                    setCreatingFolder(false);
                    setNewFilename("");
                    setTimeout(() => { isSubmittingRef.current = false; }, 0);
                  }
                }}
                onBlur={handleCreate}
                placeholder={creatingFolder ? "folder-name" : "filename.tex"}
                autoFocus
                className={cn(
                  "w-full rounded-[var(--radius-sm)] border border-[var(--color-cool-azure)] bg-transparent",
                  "px-2 py-1 text-[11px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)] outline-none",
                )}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {tree.map((node) => (
          <FileNode
            key={node.path}
            node={node}
            depth={0}
            activeDocumentId={activeDocumentId}
            onSelect={onSelect}
            expandedFolders={expandedFolders}
            toggleFolder={toggleFolder}
            onDelete={deleteDocument}
          />
        ))}

        {documents.length === 0 && !creating && (
          <div className="px-3 py-4 text-center text-[11px] text-[var(--text-muted)]">
            No files yet. Click + to create one.
          </div>
        )}
      </div>
    </div>
  );
}
